/**
 * Last-resort iframe fallback for text-oriented renderers.
 *
 * The original (pre-unification) attachment-preview-dialog had a
 * `useDirectUrl` flag: when its text fetch failed — typically a CORS
 * rejection on cross-origin storage URLs — it rendered the URL inside
 * an iframe instead of giving up. Behaviour preserved here so the
 * unification doesn't regress that path.
 *
 * Used by `MarkdownSourceRenderer`, `CsvSourceRenderer`, and
 * `CodeSourceRenderer`. PDF / image / HTML renderers don't need it —
 * they already drive themselves from `getBlobUrl` instead of `getText`.
 *
 * Renders a plain error message when the source can't supply a URL
 * (artifacts, inline sources) — there's nothing useful to put in an
 * iframe in that case.
 */
import { useEffect, useState } from "react";

import { isFileNotFoundError, type FileSource } from "../types";

export function UrlIframeFallback({
  source,
  error,
}: {
  source: FileSource;
  error: Error;
}) {
  const [url, setUrl] = useState<string | null>(null);

  /**
   * A genuinely-missing file (404) must never fall back to an iframe. The
   * iframe exists for CORS-blocked cross-origin fetches — the URL is unreadable
   * by `fetch` but still loads in a frame. For a 404 the frame would just
   * re-load the same URL and render the server's JSON error body (Chrome's JSON
   * viewer), which is the ugly output the friendly message is meant to replace.
   * Show the message instead.
   */
  const fileMissing = isFileNotFoundError(error);

  useEffect(() => {
    if (fileMissing || !source.resolveExternalUrl) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    source
      .resolveExternalUrl()
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- effect keyed on source identity
  }, [source.id]);

  if (!url) {
    /**
     * A missing file's message is already a complete sentence ("… no longer
     * exists …"), so the generic "Failed to load file." header would read as
     * redundant and slightly wrong (the load didn't fail — the file is gone).
     * Show the message on its own in that case.
     */
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        {!fileMissing && <p>Failed to load file.</p>}
        <p className={fileMissing ? "" : "text-xs opacity-70"}>{error.message}</p>
      </div>
    );
  }

  return (
    <iframe
      src={url}
      title={source.filename}
      className="w-full h-full border-0"
    />
  );
}
